import React, { createContext, useContext } from 'react';
import { useZebraWebUSB } from '../hooks/useZebraWebUSB';
import { useZebraSerial } from '../hooks/useZebraSerial';

interface ZebraContextType {
    connectHardware: () => Promise<boolean>;
    disconnectHardware: () => Promise<void>;
    printZPLHardware: (zpl: string) => Promise<boolean>;
    isUSBConnected: boolean; // Keep for compatibility
    isHardwareConnected: boolean;
    hardwareLoading: boolean;
    hardwareError: string | null;
    hardwareDeviceName: string | null;
}

const ZebraContext = createContext<ZebraContextType | null>(null);

export const ZebraProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const usb = useZebraWebUSB();
    const serial = useZebraSerial();

    const connectHardware = async () => {
        // Try USB first
        const okUSB = await usb.connect();
        if (okUSB) return true;
        
        // If USB fails with Access Denied or similar, Serial might work
        console.log('USB falló o fue cancelado, probando Serial...');
        const okSerial = await serial.connect();
        return okSerial;
    };

    const printZPLHardware = async (zpl: string) => {
        if (usb.isConnected) return await usb.printZPL(zpl);
        if (serial.isConnected) return await serial.printZPL(zpl);
        return false;
    };

    const value = {
        connectHardware,
        disconnectHardware: async () => {
            await usb.disconnect();
            await serial.disconnect();
        },
        printZPLHardware,
        isUSBConnected: usb.isConnected || serial.isConnected,
        isHardwareConnected: usb.isConnected || serial.isConnected,
        hardwareLoading: usb.loading || serial.loading,
        hardwareError: usb.error || serial.error,
        hardwareDeviceName: usb.deviceName || serial.deviceName
    };

    return (
        <ZebraContext.Provider value={value}>
            {children}
        </ZebraContext.Provider>
    );
};

export const useZebraContext = () => {
    const context = useContext(ZebraContext);
    if (!context) {
        // Fallback to avoid breaking if Provider is missing during dev
        console.warn('useZebraContext used outside of ZebraProvider');
    }
    return context;
};
